'use client';

import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';

import breakpoints from '@/lib/breakpoints';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useScrollPosition from '@/hooks/useScrollPosition';

import Hamburger from '../Hamburger';
import { KTIcon } from '../icons';
import ProgressBar from '../ProgressBar';
import TopBar from '../TopBar';

interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  isHomePage?: boolean;
  children?: ReactNode;
  displayHamburger?: React.ReactNode;
}

function Highlights(props: { shrink: boolean; isHomePage: boolean }) {
  const { shrink, isHomePage } = props;

  return (
    <div className='pointer-events-none flex items-center' data-aos='fade-in'>
      <span
        className={cn(
          'pointer-events-auto text-right font-secondary',
          'origin-right transition-transform duration-300 ease-linear',
          isHomePage ? 'text-base' : 'text-sm sm:text-base',
          shrink ? 'translate-y-6 scale-75' : 'translate-y-0 scale-100',
        )}
      >
        Highlights 2023
      </span>
    </div>
  );
}

export default function Header(props: HeaderProps) {
  const {
    isHomePage = true,
    displayHamburger = false,
    className,
    children,
    ...rest
  } = props;
  const { scrollPosition } = useScrollPosition();
  const [shrink, setShrink] = useState(false);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.lg})`);
  const [desktop, setDesktop]: [
    boolean | undefined,
    Dispatch<SetStateAction<boolean | undefined>>,
  ] = useState();

  useEffect(() => {
    setDesktop(isDesktop);
  }, [isDesktop]);

  useEffect(() => {
    setShrink(scrollPosition > 40);
  }, [scrollPosition]);

  return (
    <>
      <TopBar />
      <header
        className={cn(
          'header sticky left-0 top-0 z-[800] w-full',
          'transition-transform duration-300 ease-linear',
          shrink ? '-translate-y-10 md:-translate-y-12' : '-translate-y-0',
          className,
        )}
        {...rest}
      >
        <div className='header__topbar pointer-events-auto relative z-10 flex transform items-center justify-between bg-gray-900 py-5 pl-4 pr-6 md:px-4'>
          <KTIcon
            className={cn(
              'origin-left transition-all duration-300 ease-linear',
              'h-10 w-44 max-w-full md:h-[60px] md:w-[280px]',
              shrink
                ? 'md:scale-1/2 translate-y-5 scale-75 md:translate-y-6 md:scale-50'
                : 'translate-y-0 scale-100',
            )}
          />
          {(!isHomePage || (desktop && isHomePage)) && (
            <Highlights shrink={shrink} isHomePage={isHomePage} />
          )}
          {desktop === false && displayHamburger && (
            <Hamburger
              className={cn(shrink ? 'translate-y-5' : '-translate-y-0')}
              location='header'
            />
          )}
        </div>
        {children}
        {!desktop && isHomePage && <ProgressBar color='white' />}
      </header>
    </>
  );
}
